import React, { useEffect, useState } from "react";
import { ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, AreaChart, Area } from "recharts";
import { Thermometer, Snowflake, Sun, CloudRain, AlertCircle } from "lucide-react";
import { api, fmtDec, fmtInt } from "../lib/api";
import KPICard from "../components/KPICard";

export default function Climatologia() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/climatologia").then((r) => setData(r.data)); }, []);
  if (!data) return <div className="text-gray-500">Cargando climatología…</div>;

  const a = data.anual;

  return (
    <div className="space-y-6" data-testid="climatologia-page">
      <div>
        <div className="text-xs font-semibold tracking-widest text-gray-500 uppercase">Climatología histórica</div>
        <h1 className="section-title mt-1">Climatología · año {data.anio}</h1>
        <p className="section-sub">
          Reanálisis ERA5-Land (Open-Meteo) para la ubicación de referencia configurada. El consumo eléctrico de un autobús aumenta significativamente con
          temperaturas extremas por HVAC: calefacción si la media diaria &lt; 14 °C, refrigeración si &gt; 22 °C.
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-5">
        <KPICard testId="kpi-tmedia" label="Temperatura media anual" value={`${fmtDec(a.temp_media, 1)} °C`} icon={Thermometer} tone="blue" />
        <KPICard testId="kpi-frio" label="Días calefacción" value={fmtInt(a.dias_calefaccion)} hint="< 8 °C media" icon={Snowflake} tone="blue" />
        <KPICard testId="kpi-calor" label="Días refrigeración" value={fmtInt(a.dias_refrigeracion)} hint="> 26 °C media" icon={Sun} tone="red" />
        <KPICard testId="kpi-extremos" label="Días extremos" value={fmtInt(a.dias_extremos_calor + a.dias_extremos_frio)} hint={`${a.dias_extremos_calor} calor + ${a.dias_extremos_frio} frío`} icon={AlertCircle} tone="red" />
        <KPICard testId="kpi-factor" label="Factor climático anual" value={`+${(a.factor_climatico_medio * 100).toFixed(2)} %`} hint="sobrecoste medio kWh/km" icon={Thermometer} tone="green" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="crtm-card p-5">
          <h3 className="font-heading font-semibold text-[15px] mb-3">Temperatura mensual (año de referencia)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={data.mensual}>
              <CartesianGrid stroke="#F3F4F6" vertical={false} />
              <XAxis dataKey="mes" tick={{ fontSize: 12, fill: "#4B5563" }} />
              <YAxis tick={{ fontSize: 11, fill: "#9CA3AF" }} unit=" °C" />
              <Tooltip contentStyle={{ background: "white", border: "1px solid #E5E7EB", borderRadius: 8, fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Area type="monotone" dataKey="temp_max_media" name="Tª máx" fill="#FECACA" stroke="#DC2626" strokeWidth={1.5} />
              <Area type="monotone" dataKey="temp_min_media" name="Tª mín" fill="#BFDBFE" stroke="#1D4ED8" strokeWidth={1.5} />
              <Line type="monotone" dataKey="temp_media" name="Tª media" stroke="#14532D" strokeWidth={2.5} dot={{ r: 4, fill: "#14532D" }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        <div className="crtm-card p-5">
          <h3 className="font-heading font-semibold text-[15px] mb-3">Sobrecoste energético HVAC por mes</h3>
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={data.mensual}>
              <CartesianGrid stroke="#F3F4F6" vertical={false} />
              <XAxis dataKey="mes" tick={{ fontSize: 12, fill: "#4B5563" }} />
              <YAxis yAxisId="left" tick={{ fontSize: 11, fill: "#9CA3AF" }} tickFormatter={(v) => `+${(v * 100).toFixed(0)}%`} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: "#9CA3AF" }} unit=" d" />
              <Tooltip contentStyle={{ background: "white", border: "1px solid #E5E7EB", borderRadius: 8, fontSize: 12 }}
                formatter={(v, name) => name.includes("kWh") ? `+${(v * 100).toFixed(2)} %` : v} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar yAxisId="right" dataKey="dias_frio_severo" name="Días frío" fill="#3B82F6" maxBarSize={22} radius={[3,3,0,0]} />
              <Bar yAxisId="right" dataKey="dias_calor_severo" name="Días calor" fill="#DC2626" maxBarSize={22} radius={[3,3,0,0]} />
              <Line yAxisId="left" type="monotone" dataKey="factor_climatico_mensual" name="Sobrecoste kWh/km" stroke="#14532D" strokeWidth={2.5} dot={{ r: 4 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="crtm-card overflow-hidden">
        <table className="crtm-table">
          <thead>
            <tr>
              <th>Mes</th>
              <th>Tª media</th>
              <th>Tª min</th>
              <th>Tª max</th>
              <th>Precipitación</th>
              <th>Días &lt; 8 °C</th>
              <th>Días &gt; 26 °C</th>
              <th>Sobrecoste kWh/km</th>
            </tr>
          </thead>
          <tbody>
            {data.mensual.map((m) => (
              <tr key={m.mes_num}>
                <td className="font-semibold">{m.mes}</td>
                <td className="font-mono">{fmtDec(m.temp_media, 1)} °C</td>
                <td className="font-mono text-blue-700">{fmtDec(m.temp_min_media, 1)} °C</td>
                <td className="font-mono text-red-700">{fmtDec(m.temp_max_media, 1)} °C</td>
                <td className="font-mono">{fmtDec(m.precipitacion_total, 0)} mm</td>
                <td className="font-mono">{m.dias_frio_severo}</td>
                <td className="font-mono">{m.dias_calor_severo}</td>
                <td className="font-mono font-semibold">+{(m.factor_climatico_mensual * 100).toFixed(2)} %</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="crtm-card p-5">
        <div className="flex items-start gap-3">
          <CloudRain size={20} className="text-green-700 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-gray-700 leading-relaxed">
            <strong>Modelo de penalización HVAC</strong>: en la franja confortable 14–22 °C el sobrecoste es 0%.
            Por debajo se aplica un +1,8% por cada °C de calefacción; por encima un +2,0% por °C de refrigeración (techo +35–40%).
            Activa "Aplicar climatología" en la página <em>Escenarios</em> para recalcular el factor kWh/km con el dato anual real del año 2025
            (<strong>+{(a.factor_climatico_medio * 100).toFixed(2)}%</strong>).
          </div>
        </div>
      </div>
    </div>
  );
}
